import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'
import { SoftNav } from '@newrelic/browser-agent/features/soft_navigations'
import { Injectable } from '@angular/core'
import { JSErrors } from '@newrelic/browser-agent/features/jserrors'
import { environment } from '@environments/environment'

export enum PageAction {
    AddWalkInFormLoaded = 'AddWalkIn.FormLoaded',
    AddWalkInAreaChanged = 'AddWalkIn.AreaChanged',
    AddWalkInPartySizeChanged = 'AddWalkIn.PartySizeChanged',
    AddWalkInReasonChanged = 'AddWalkIn.ReasonChanged',
    AddWalkInChooseOwnTablesSelected = 'AddWalkIn.ChooseOwnTablesSelected',
    AddWalkInFormCancelled = 'AddWalkIn.FormCancelled',
    AddWalkInAddToDiary = 'AddWalkIn.AddToDiary',
    SettingsSearchOpened = 'SettingsSearch.Opened',
    SettingsSearchResultClicked = 'SettingsSearch.ResultClicked',
    SettingsSearchDismissed = 'SettingsSearch.Dismissed',
    ScheduleModeEntered = 'ScheduleMode.Entered',
    ScheduleModeExited = 'ScheduleMode.Exited',
    BookingSlotXAxisSaved = 'BookingSlot.XAxisSaved',
    BookingSlotXAxisDiscarded = 'BookingSlot.XAxisDiscarded',
    BookingSlotClosureOpened = 'BookingSlot.ClosureOpened',
    BookingSlotClosureSaved = 'BookingSlot.ClosureSaved',
    BookingSlotLimitOverrideOpened = 'BookingSlot.LimitOverrideOpened',
    BookingSlotLimitOverrideSaved = 'BookingSlot.LimitOverrideSaved',
}

@Injectable({
    providedIn: 'root',
})
export class LoggingService {
    private browserAgent?: BrowserAgent
    private pendingUserId?: string
    private pendingOrganisationId?: string
    private pendingVenueId?: string
    private pendingCustomAttributes: Map<string, string | number | boolean> = new Map()

    setUp() {
        const applicationId = environment.newRelicApplicationId
        if (applicationId.length === 0) {
            return
        }
        const options = this.makeOptions()
        options.info.applicationID = applicationId
        options.loader_config.applicationID = applicationId
        options.loader_config.agentID = applicationId
        this.browserAgent = new BrowserAgent(options)
        this.applyPendingAttributes()
    }

    setUserId(userId: string) {
        this.pendingUserId = userId
        if (this.browserAgent) {
            this.browserAgent.setUserId(userId)
        }
    }

    setOrganisationId(organisationId: string) {
        this.pendingOrganisationId = organisationId
        if (this.browserAgent) {
            this.browserAgent.setCustomAttribute('organisationId', organisationId, true)
        }
    }

    setVenueId(venueId: string) {
        this.pendingVenueId = venueId
        if (this.browserAgent) {
            this.browserAgent.setCustomAttribute('venueId', venueId, true)
        }
    }

    setCustomAttribute(key: string, value: string | number | boolean) {
        this.pendingCustomAttributes.set(key, value)
        if (this.browserAgent) {
            this.browserAgent.setCustomAttribute(key, value, true)
        }
    }

    trackAction(actionName: PageAction, attributes?: Map<string, string | number | boolean>) {
        if (this.browserAgent) {
            this.browserAgent.addPageAction(actionName, { ...(attributes ? Object.fromEntries(attributes) : {}) })
        }
    }

    private applyPendingAttributes() {
        if (!this.browserAgent) {
            return
        }
        if (this.pendingUserId) {
            this.browserAgent.setUserId(this.pendingUserId)
        }
        if (this.pendingOrganisationId) {
            this.browserAgent.setCustomAttribute('organisationId', this.pendingOrganisationId, true)
        }
        if (this.pendingVenueId) {
            this.browserAgent.setCustomAttribute('venueId', this.pendingVenueId, true)
        }
        this.pendingCustomAttributes.forEach((value, key) => {
            this.browserAgent!.setCustomAttribute(key, value, true)
        })
    }

    private makeOptions() {
        return {
            init: {
                ajax: { deny_list: ['bam.eu01.nr-data.net'] },
                distributed_tracing: { enabled: true },
                privacy: { cookies_enabled: true },
            },
            info: {
                beacon: 'bam.eu01.nr-data.net',
                errorBeacon: 'bam.eu01.nr-data.net',
                licenseKey: 'NRJS-42809ed77caeb693bfa',
                applicationID: '',
                sa: 1,
            },
            loader_config: {
                accountID: '4432793',
                trustKey: '4432793',
                agentID: '',
                licenseKey: 'NRJS-42809ed77caeb693bfa',
                applicationID: '',
            },
            features: [
                JSErrors,
                SoftNav,
            ],
        }
    }
}
